import React, { useId } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { EstadoError, EstadoVacio } from "../../components/base";

// Texto solo para lectores de pantalla.
const ocultoVisual = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

/**
 * Tarjeta de una sección del panel (un gráfico): título (h2), y dentro el estado que toque.
 * Orden: carga, error, vacío, datos.
 *
 * @param {object} props
 * @param {string} props.titulo
 * @param {boolean} props.cargando Esqueleto del tamaño del gráfico.
 * @param {boolean|"conexion"} props.error `EstadoError` con "Reintentar"; con "conexion", dice que no hubo conexión.
 * @param {boolean} props.vacio `EstadoVacio`: la carga salió bien pero no hay datos.
 * @param {string} [props.descripcionVacio] Qué se vería aquí.
 * @param {Function} [props.onReintentar]
 * @param {number|object} [props.altura] Alto del gráfico (px o valor responsive de `sx`).
 * @param {React.ReactNode} [props.resumen] Cifra de la sección (p. ej. el total), a la derecha del
 *   título; solo se muestra con datos.
 * @param {React.ReactNode} props.children El gráfico.
 */
const SeccionPanel = ({
  titulo,
  resumen,
  cargando,
  error,
  vacio,
  descripcionVacio,
  onReintentar,
  altura = 300,
  children,
}) => {
  const idTitulo = useId();

  let contenido;
  if (cargando) {
    contenido = (
      <Box role="status" aria-live="polite">
        <Box component="span" sx={ocultoVisual}>
          Cargando…
        </Box>
        <Skeleton
          variant="rounded"
          aria-hidden="true"
          sx={{
            height: altura,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      </Box>
    );
  } else if (error) {
    contenido =
      error === "conexion" ? (
        <EstadoError
          titulo="No se pudo conectar con el servidor"
          onReintentar={onReintentar}
        />
      ) : (
        <EstadoError onReintentar={onReintentar} />
      );
  } else if (vacio) {
    contenido = <EstadoVacio descripcion={descripcionVacio} />;
  } else {
    // `position: relative` y una altura fija: Chart.js llena el contenedor (maintainAspectRatio: false).
    contenido = (
      <Box sx={{ position: "relative", height: altura }}>{children}</Box>
    );
  }

  return (
    <Paper
      component="section"
      aria-labelledby={idTitulo}
      sx={{
        p: { xs: 2, sm: 3 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          columnGap: 2,
          rowGap: 0.5,
          mb: 2,
        }}
      >
        <Typography
          id={idTitulo}
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {titulo}
        </Typography>
        {resumen && !cargando && !error && !vacio && (
          <Typography
            variant="monto"
            component="p"
            sx={{ fontSize: "1.125rem", color: "text.primary" }}
          >
            {resumen}
          </Typography>
        )}
      </Box>
      {/* Si la fila hace la tarjeta más alta que su gráfico, el gráfico queda centrado en el alto. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {contenido}
      </Box>
    </Paper>
  );
};

export default SeccionPanel;
